/**
 * Impact & Metrics Translations Module (Spanish)
 * 
 * Contains: YFI Formula, YFI Rankings, Performance KPIs, Advanced Metrics, Engagement Metrics
 * 
 * @module impact
 * @language es
 */

export const impact = {
  impactMetrics: {
    pageTitle: "Impacto y Métricas",
    pageSubtitle: "Midiendo el Liderazgo Joven, Salud Mental y Oportunidad Económica en 114 países JCI"
  },
  fiiFormula: {
    stats: {
      mentalDisorders: "1 de 7 afectados por trastornos mentales",
      mentalHealthLimiting: "68% citan salud mental como limitante",
      emergingAccess: "Mercados emergentes (10-30% acceso)",
      digitalSkillsGap: "92% de trabajos necesitan habilidades digitales (solo 30% capacitados)",
      youthVsAdult: "13% juventud global (vs 4.9% adultos)",
      neetCrisis: "256M jóvenes (20.4%) no en educación/trabajo",
      ssaAspiration: "52% de jóvenes África Subsahariana quieren iniciar negocio",
      fundingGap: "Solo 3% obtienen financiamiento (brecha $1.7T global)",
      womenGap: "Mujeres emprendedoras 35% subrepresentadas"
    },
    reasoning: {
      mentalHealth: "Ponderación alta refleja importancia fundamental - salud mental habilita otras capacidades",
      workforce: "Ponderación significativa refleja empleo como puerta a independencia económica",
      entrepreneurship: "Ponderación significativa refleja potencial innovación y necesidad autoempleo"
    },
    mentalHealthStat1: "1 de 7 afectados por trastornos mentales",
    mentalHealthStat2: "68% citan salud mental como limitante",
    mentalHealthStat3: "Mercados emergentes (10-30% acceso)",
    workforceStat1: "92% de trabajos necesitan habilidades digitales (solo 30% capacitados)",
    workforceStat2: "13% desempleo juvenil global (vs 4.9% adultos)",
    workforceStat3: "256M jóvenes (20.4%) no en educación/trabajo",
    entrepreneurshipStat1: "52% de jóvenes África Subsahariana quieren iniciar negocio",
    entrepreneurshipStat2: "Solo 3% obtienen financiamiento (brecha $1.7T global)",
    entrepreneurshipStat3: "Mujeres emprendedoras 35% subrepresentadas",
    mentalHealthReasoning: "Ponderación alta refleja importancia fundamental - salud mental habilita otras capacidades",
    workforceReasoning: "Ponderación significativa refleja empleo como puerta a independencia económica",
    entrepreneurshipReasoning: "Ponderación significativa refleja potencial innovación y necesidad autoempleo"
  },
  engagement: {
    title: "Métricas de Participación",
    subtitle: "Análisis de participación y actividad de miembros",
    eventParticipation: "Participación en Eventos",
    volunteerHours: "Horas de Voluntariado",
    projectContributions: "Contribuciones a Proyectos",
    monthlyActiveUsers: "Usuarios Activos Mensuales"
  },
  network: {
    title: "Análisis de Red",
    subtitle: "Conexiones globales y patrones de colaboración",
    totalConnections: "Conexiones Totales",
    activeProjects: "Proyectos Activos",
    strategicPartnerships: "Alianzas Estratégicas",
    knowledgeTransfer: "Transferencia de Conocimiento",
    collectiveImpact: "Impacto Colectivo",
    progress: "Progreso",
    target: "Meta"
  },
  trends: {
    title: "Análisis de Tendencias y Predicciones",
    subtitle: "Evolución histórica de JCI y proyecciones basadas en IA para 2026-2030",
    historical: "Evolución Histórica (2015-2025)",
    predictions: "Proyecciones 2026-2030",
    confidence: "confianza",
    keyTrends: "Tendencias Clave Identificadas",
    sustainedGrowth: "Crecimiento Sostenido",
    digitalRevolution: "Revolución Digital",
    wellbeingFocus: "Enfoque en Bienestar",
    sustainability: "Sostenibilidad",
    seasonalPatterns: "Patrones Estacionales",
    enrollments: "Inscripciones",
    projects: "Proyectos",
    events: "Eventos",
    members: "Miembros",
    countries: "Países",
    imh: "ISM"
  },
  status: {
    strong: "Fuerte",
    growing: "Creciendo",
    expanding: "Expandiendo",
    stable: "Estable",
    emerging: "Emergente",
    active: "Activo",
    expansion: "Expansión",
    pilot: "Piloto",
    beta: "Beta"
  },
  advancedMetrics: {
    title: "Métricas Avanzadas",
    subtitle: "Análisis detallado de rendimiento, conversión e indicadores clave para Visión 2040",
    conversionFunnel: "Embudo de Conversión",
    websiteVisitors: "Visitantes del Sitio",
    interested: "Interesados",
    candidates: "Candidatos",
    interviewed: "Entrevistados",
    activeMembers: "Miembros Activos",
    totalConversionRate: "Tasa de Conversión Total",
    costPerAcquisition: "Costo por Adquisición",
    programROI: "ROI del Programa",
    performanceTrends: "Tendencias de Rendimiento",
    members: "Miembros",
    countries: "Países",
    programs: "Programas",
    keyPerformanceIndicators: "Indicadores Clave de Rendimiento",
    retentionRate: "Tasa de Retención",
    membersActive12Months: "Miembros activos por 12+ meses",
    digitalEngagement: "Participación Digital",
    onlineEventParticipation: "Participación en eventos en línea",
    memberSatisfaction: "Satisfacción de Miembros",
    avgProgramEvaluation: "Evaluación promedio de programas",
    averageLeadershipTime: "Tiempo Promedio de Liderazgo",
    joiningToFirstLeadership: "Desde ingreso a primera posición de liderazgo",
    target: "Meta",
    progress: "Progreso"
  },
  performanceKPIs: {
    title: "KPIs de Rendimiento",
    subtitle: "Indicadores clave de rendimiento y metas para 2025",
    updatedAgo: "Actualizado",
    memberRetentionRate: "Tasa de Retención de Miembros",
    annualRetentionVsTarget: "Retención anual de miembros vs meta",
    programROI: "ROI del Programa",
    trainingProgramsROI: "Retorno de inversión para programas de capacitación",
    newMarkets: "Nuevos Mercados",
    newCountriesEntered: "Nuevos países ingresados este año",
    communityImpactScore: "Puntuación de Impacto Comunitario",
    aggregateCommunityImpact: "Medición de impacto comunitario agregado",
    memberEngagement: "Participación de Miembros",
    activeParticipationJCI: "Participación activa en actividades JCI",
    leadershipPipeline: "Pipeline de Liderazgo",
    membersInLeadershipTrack: "Miembros en desarrollo de liderazgo",
    performance: "rendimiento",
    growth: "crecimiento",
    impact: "impacto",
    engagement: "participación",
    target: "Meta"
  },
  aiInsights: {
    title: "Centro de Insights IA",
    subtitle: "Analítica predictiva, recomendaciones estratégicas y escenarios hipotéticos para decisiones inteligentes",
    aiChat: "Chat IA",
    recommendations: "Recomendaciones",
    insights: "Insights",
    whatIf: "Qué-Si",
    jciAssistant: "Asistente Inteligente JCI",
    askAboutData: "Pregunta sobre datos, tendencias y estrategias",
    askPlaceholder: "Pregunta sobre tendencias, análisis o estrategias...",
    greeting: "¡Hola! Soy el asistente de insights JCI. ¿Cómo puedo ayudarte a analizar datos y descubrir oportunidades estratégicas?",
    analyzing: "Analizando tu pregunta sobre",
    growthPotential: "potencial de crecimiento en los próximos 18 meses",
    priority: "Prioridad",
    confidence: "Confianza",
    viewDetails: "Ver Detalles",
    expectedImpact: "Impacto Esperado",
    estimatedTime: "Tiempo estimado",
    simulateScenario: "Simular Escenario",
    strategicExpansion: "Expansión Estratégica en América Latina",
    strategicExpansionDesc: "Los datos indican 40% de potencial de crecimiento inexplorado en países como Colombia y Perú.",
    digitalMentalHealth: "Programa Digital de Salud Mental",
    digitalMentalHealthDesc: "Implementar plataforma digital aumentaría participación 60% en comunidades jóvenes.",
    techPartnerships: "Alianzas Tecnológicas",
    techPartnershipsDesc: "Integración con startups locales podría acelerar programas de innovación 45%.",
    growth: "Crecimiento",
    wellbeing: "Bienestar",
    innovation: "Innovación",
    millennialEngagement: "Participación Millennials",
    millennialEngagementDesc: "Crecimiento acelerado en participación de 25-30 años",
    sustainableProjects: "Proyectos Sostenibles",
    sustainableProjectsDesc: "Iniciativas ESG mostraron ROI superior",
    regionalRetention: "Retención Regional",
    regionalRetentionDesc: "Algunas regiones necesitan atención especial",
    whatIfLeadership: "¿Qué pasa si aumentamos inversión en programas de liderazgo 25%?",
    whatIfLeadershipImpact: "35% aumento en calidad de liderazgo, 2.3x más participación en eventos",
    whatIfAI: "¿Qué pasa si implementamos IA en todos los procesos administrativos?",
    whatIfAIImpact: "40% reducción en tiempo administrativo, $2.1M ahorro anual",
    budget: "Presupuesto",
    months: "meses",
    focusYoungLeaders: "Enfoque en líderes jóvenes",
    totalAutomation: "Automatización total",
    training: "Capacitación",
    gradualMigration: "Migración gradual"
  },
  homeGlobalPresence: {
    title: "Presencia Global JCI",
    subtitle: "Activos en 114 países en 4 regiones",
    regions: "Regiones",
    countries: "Países",
    members: "Miembros",
    localOrgs: "Orgs Locales"
  },
  fii: {
    title: "Índice de Futuros de la Juventud (YFI)",
    measuringYouthOpportunity: "Midiendo Oportunidades de la Juventud en 114 Países",
    scale: "Escala: 0-100 (Mayor = Mejor Oportunidad de la Juventud)",
    mentalHealth: "Salud Mental",
    workforceEducation: "Fuerza Laboral y Educación",
    entrepreneurshipSupport: "Apoyo al Emprendimiento",
    mentalHealthQuote: "La salud mental es la base sobre la cual se construyen todas las demás capacidades.",
    workforceQuote: "El empleo es la puerta de entrada a la independencia económica.",
    entrepreneurshipQuote: "La innovación y el autoempleo son caminos esenciales para la oportunidad de la juventud.",
    globalCrisis: "Crisis Global",
    employmentBarrier: "Barrera de Empleo",
    mostVulnerable: "Más Vulnerables",
    skillGap: "Brecha de Habilidades",
    unemployment: "Desempleo Juvenil",
    neetCrisis: "Crisis NINI",
    aspiration: "Aspiración Emprendedora",
    fundingGap: "Brecha de Financiamiento",
    genderGap: "Brecha de Género",
    whyThisWeighting: "¿Por Qué Esta Ponderación?",
    ofTotalScore: "del puntaje total",
    weight: "Peso",
    source: "Fuente",
    indicators: "Indicadores",
    countriesMeasured: "Países Medidos",
    jciNationalOrgs: "Orgs Nacionales JCI",
    completeData: "Datos Completos",
    countries: "países",
    averageFII: "YFI Promedio",
    highest: "Más Alto",
    lowest: "Más Bajo",
    pointRange: "Rango de Puntos",
    countriesRanked: "Países Clasificados",
    excellent: "Excelente",
    good: "Bueno",
    moderate: "Moderado",
    deficient: "Deficiente",
    challenges: "Desafíos",
    critical: "Crítico",
    globalRankings: "Índice Global YFI",
    searchCountry: "Buscar país...",
    allRegions: "Todas las Regiones",
    rank: "Rango",
    country: "País",
    region: "Región",
    score: "Puntuación",
    classification: "Clasificación",
    
    // Component Breakdown & Benchmarks
    componentBreakdown: "Desglose por Componente",
    vsBenchmarks: "vs Referencias",
    vsGlobalAvg: "vs Promedio Global",
    globalAverage: "Promedio Global",
    data: "Datos",
    complete: "Completo",
    limited: "Limitado",
    
    // Legend labels
    legendGood: "Bueno",
    legendModerate: "Moderado",
    legendChallenges: "Desafíos",
    legendCritical: "Crítico",
    
    useFIIData: "Usar Datos YFI",
    useFIIDataDesc: "Descarga datos del índice y documentación metodológica para investigación y promoción",
    exportRankings: "Exportar Índice (CSV)",
    downloadMethodology: "Descargar Metodología (PDF)",
    comingSoon: "Próximamente",
    methodologyComingSoon: "PDF de metodología próximamente",
    researchCitations: "Citas de Investigación",
    sourceWhoDescription: "Acceso a servicios de salud mental, brechas de tratamiento, cobertura de políticas",
    sourceIloDescription: "Desempleo juvenil, tasas de NEET, calidad de la fuerza laboral",
    sourceGemDescription: "Emprendimiento en etapa temprana, entorno empresarial",
    sourceWorldBankDescription: "Datos de apoyo para la dimensión de emprendimiento",
    aboutMethodology: "Sobre la Metodología",
    aboutMethodologyDesc: "El YFI utiliza datos verificados de OMS, OIT, Banco Mundial y otras fuentes autorizadas.",
    howToCite: "Cómo Citar",
    citationText: "JCI Development Councils Youth Futures Observatory (2025). Índice de Futuros de la Juventud.",
    insightsTitle: "Perspectivas Estratégicas",
    insightsSubtitle: "Hallazgos clave del análisis YFI",
    finding: "Hallazgo",
    dataEvidence: "Evidencia de Datos",
    implication: "Implicación",
    actionForJCI: "Acción para JCI",
    insight1Title: "Salud Mental como Base",
    insight2Title: "Desajuste Habilidades-Empleo",
    insight3Title: "Disparidades Regionales",
    insight4Title: "Paradoja del Emprendimiento",
    insight5Title: "Brechas de Género Persisten",
    dataVintage: "Fecha de Datos",
    dataVintageDesc: "Datos 2023-2024 de fuentes internacionales verificadas",
    methodologyTitle: "Metodología YFI",
    methodologySubtitle: "Cómo calculamos el Índice de Impacto Futuro",
    formulaExplanation: "Explicación de la Fórmula",
    pillarWeights: "Ponderación de Pilares",
    dataSourcesTitle: "Fuentes de Datos",
    calculationFormula: "Fórmula de Cálculo",
    threeDimensions: "Las Tres Dimensiones",
    officialDataSources: "Fuentes de Datos Oficiales",
    dataCoverage: "Cobertura de Datos",
    dataCoverageDesc: "El YFI cubre 114 países miembros de JCI. La disponibilidad de datos varía por país e indicador.",
    openData: "Datos Abiertos",
    openDataDesc: "Todos los datos fuente están disponibles públicamente de instituciones autorizadas.",
    zeroSynthetic: "Cero Datos Sintéticos",
    zeroSyntheticDesc: "Usamos solo datos reales y verificados - nunca valores generados por IA o estimados.",
    fullTransparency: "Transparencia Total",
    fullTransparencyDesc: "Metodología completa y cálculos están documentados públicamente.",
    scientificRigor: "Rigor Científico",
    scientificRigorDesc: "Metodología revisada y validada por socios de investigación.",
    regionalPerformance: "Rendimiento Regional",
    topPerformers: "Mejores Resultados",
    bottomPerformers: "Resultados Más Bajos",
    keyTakeaways: "Conclusiones Clave"
  },
  globalFIIMap: {
    title: "Mapa Global YFI",
    subtitle: "Explora métricas de oportunidad de la juventud en todos los países JCI",
    jciCountries: "Países JCI",
    totalMembers: "Miembros Totales",
    completeData: "Datos Completos",
    globalRegions: "Regiones Globales",
    regionalOverview: "Resumen Regional",
    countries: "Países",
    avgFII: "YFI Promedio",
    leader: "Líder",
    searchCountry: "Buscar país...",
    allRegions: "Todas las Regiones",
    allClassifications: "Todas las Clasificaciones",
    regionAsiaPacific: "Asia y el Pacífico",
    regionEurope: "Europa",
    regionAmericas: "América",
    regionAfricaMe: "África y Oriente Medio",
    excellent: "Excelente",
    good: "Bueno",
    moderate: "Moderado",
    deficient: "Deficiente",
    critical: "Crítico",
    countriesTotal: "Total de Países",
    matching: "coincidiendo",
    fiiDataset: "Dataset YFI",
    withCompleteData: "con datos completos",
    limitedData: "Datos Limitados",
    members: "miembros"
  },
  fiiInsights: {
    insight: "Perspectiva",
    sourceAttribution: "Informe de Impacto JCI 2025 | Análisis Regional YFI 2024",
    priority: {
      critical: "Crítico",
      high: "Alto",
      medium: "Medio"
    },
    mentalHealth: {
      finding: "La salud mental es el mejor predictor del puntaje YFI general. Los países con altos puntajes de salud mental consistentemente se clasifican mejor.",
      mhScoreVariation: "Variación del Puntaje de SM",
      wfEtScoreVariation: "Variación de Puntajes de FL & Emp",
      potentialImprovement: "Mejora Potencial del YFI",
      implication: "Invertir en salud mental genera el mayor retorno en oportunidad de la juventud general.",
      action: "Priorizar programas de salud mental en iniciativas JCI, especialmente en regiones de bajo puntaje."
    },
    skillsMismatch: {
      finding: "A pesar de la alta matrícula educativa, el desempleo juvenil permanece elevado debido al desajuste de habilidades.",
      youthInEducation: "Jóvenes en Educación",
      youthUnemployment: "Tasa de Desempleo Juvenil",
      neetPopulation: "Población NINI",
      digitalSkillsJobs: "Empleos que Necesitan Habilidades Digitales",
      implication: "La educación tradicional no está preparando a los jóvenes para la fuerza laboral moderna.",
      action: "Desarrollar programas de capacitación basados en habilidades enfocados en competencias digitales y empresariales."
    },
    regionalDisparities: {
      finding: "Existen brechas significativas entre regiones, con África-Medio Oriente enfrentando los mayores desafíos.",
      mentalHealthGap: "Brecha de Salud Mental (Europa vs África-MO)",
      workforceGap: "Brecha de Fuerza Laboral",
      entrepreneurshipGap: "Brecha de Emprendimiento",
      implication: "Un enfoque único no funcionará; se necesitan estrategias regionales.",
      action: "Desarrollar programas específicos por región y aumentar la asignación de recursos a áreas desatendidas."
    },
    entrepreneurship: {
      finding: "Existe alta aspiración emprendedora en regiones en desarrollo pero el acceso al capital es severamente limitado.",
      ssaRate: "Tasa de Emprendimiento en ASS",
      financingGap: "Brecha de Financiamiento Global",
      africaMeShare: "Participación África-MO en Startups Globales",
      youthWanting: "Jóvenes Queriendo Iniciar Negocio",
      implication: "Existe potencial emprendedor sin explotar, restringido por el acceso a financiamiento.",
      action: "Crear programas de microfinanciamiento y conectar jóvenes emprendedores con inversores."
    },
    gender: {
      finding: "Las mujeres enfrentan desventajas compuestas en las tres dimensiones del YFI.",
      depressionRates: "Tasas de Depresión de Mujeres",
      womenUnemployment: "Desempleo de Mujeres",
      neetWomen: "Participación de Mujeres en NINI",
      womenLedStartups: "Startups Lideradas por Mujeres",
      vcToWomen: "Financiamiento VC para Mujeres",
      implication: "Las barreras específicas de género requieren intervenciones dirigidas.",
      action: "Lanzar programas de liderazgo y emprendimiento enfocados en mujeres."
    }
  },
  fiiMethodology: {
    formulaExplanation: "El YFI se calcula como un promedio ponderado de tres dimensiones: Salud Mental (40%), Fuerza Laboral y Educación (30%), y Apoyo al Emprendimiento (30%).",
    mentalHealth: {
      description: "Mide el bienestar mental juvenil, acceso a servicios de salud mental y bienestar psicológico general."
    },
    workforce: {
      description: "Evalúa tasas de empleo juvenil, calidad de educación, desarrollo de habilidades y acceso al mercado laboral."
    },
    entrepreneurship: {
      description: "Evalúa el ambiente de negocios, acceso a financiamiento, infraestructura digital y ecosistema de startups."
    },
    indicators: {
      mentalDisorderPrevalence: "Prevalencia de Trastornos Mentales",
      youthSuicideRate: "Tasa de Suicidio Juvenil",
      mentalHealthServiceAccess: "Acceso a Servicios de Salud Mental",
      mentalWellbeingIndex: "Índice de Bienestar Mental",
      youthUnemploymentRate: "Tasa de Desempleo Juvenil",
      neetRate: "Tasa NINI",
      tertiaryEducation: "Matrícula en Educación Superior",
      humanCapitalIndex: "Índice de Capital Humano",
      easeOfDoingBusiness: "Facilidad para Hacer Negocios",
      totalEntrepreneurialActivity: "Actividad Emprendedora Total",
      accessToFinancing: "Acceso a Financiamiento",
      digitalInfrastructure: "Infraestructura Digital"
    },
    dataTypes: {
      education: "Educación",
      multiDimensional: "Multidimensional",
      wellbeing: "Bienestar",
      digitalInfrastructure: "Infraestructura Digital"
    },
    access: {
      publicApiDownloads: "API Pública y Descargas",
      ilostatPublicApi: "API Pública ILOSTAT",
      publicDataPortal: "Portal de Datos Públicos",
      freeApiAccess: "Acceso Gratuito a API",
      publicReports: "Informes Públicos",
      publicData: "Datos Públicos",
      annualPublicRelease: "Publicación Pública Anual",
      publicStatistics: "Estadísticas Públicas"
    }
  }
} as const;
